import type { FormEvent } from 'react'
import type { SingleMatch, SearchMatchesData } from 'types/match'

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { addParamToPath, dimmBackground, formatDate } from 'utils'
import { clearURLQueryParam, getURLQueryParam, replaceURLParams } from 'utils/urlParams'
import copy from 'copy.json'
import { STATUS } from 'common'
import { adminPanelPaths } from 'paths'
import { CheckedInShield } from 'icons'

import Button from 'components/Button'
import Input from 'components/Input'
import InputLabel from 'components/InputLabel'
import Pagination from 'components/Pagination'
import SearchScreenNavigation from 'components/SearchScreenNavigation'
import Spinner from 'components/Spinner'
import Status from 'components/Status'
import Table from 'components/Table'
import PageContainer from 'components/PageContainer'
import Scroll from 'components/Scroll'
import Multiselect, { getSelectedValues, OptionProps } from 'components/Multiselect'

const texts_form = copy.common.form

// This is needed to clear location.state after refresh
window.history.replaceState(null, '')

const defaultSearchValues: SearchMatchesData = {
  created_by: null,
  statuses: [],
  page_number: 0,
  demand_id: null,
  offer_id: null
}

const matchStatus: SingleMatch['status'][] = [
  'ACTIVE',
  'RESERVED',
  'BLOCKED',
  'ACCEPTED',
  'FINALIZED',
  'DELETED'
]

export default function SearchMatchesScreen() {
  const navigate = useNavigate()
  const { api, fieldErrors, clearFieldErrors } = useStore()
  const location = useLocation()
  const timeout = useRef(0)
  const [searchMatchesData, setSearchMatchesData] = useState<SingleMatch[] | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusOptions, setStatusOptions] = useState<OptionProps[]>(
    matchStatus.map(status => ({
      name: STATUS[status],
      value: status,
      isChecked: false
    }))
  )

  const noResults = searchMatchesData && searchMatchesData.length === 0
  const tableIsVisible = searchMatchesData && searchMatchesData.length > 0
  const paginationIsVisible = tableIsVisible && totalPages > 1
  const spinnerIsVisible = loading && !searchMatchesData

  defaultSearchValues.page_number = getURLQueryParam('page')

  const [searchFormValues, setSearchFormValues] = useState<SearchMatchesData>({
    ...defaultSearchValues,
    demand_id: location.state
  })

  const handlePageChange = (currentStep: number) => {
    replaceURLParams(currentStep, 'page', navigate)
    setSearchFormValues({ ...searchFormValues, page_number: currentStep - 1 })
  }

  const handleGetMatch = (matchId: number) => {
    navigate(addParamToPath(adminPanelPaths.matches, matchId), {
      state: { rememberBackPage: true }
    })
  }

  const handleSearch = (event: FormEvent<HTMLInputElement>) => {
    clearFieldErrors(event.currentTarget.name)
    clearTimeout(timeout.current)
    clearURLQueryParam('page', navigate)

    const form = document.getElementById('search-matches-form') as HTMLFormElement
    const values = Array.from(new FormData(form)).reduce(
      (result, [name, value]) => ({ ...result, [name]: value !== '' ? value : null }),
      {} as SearchMatchesData
    )

    timeout.current = window.setTimeout(
      () => setSearchFormValues(state => ({ ...state, ...values })),
      500
    )
  }

  const handleStatusChange = (options: OptionProps[]) => {
    setStatusOptions(options)
    setSearchFormValues(state => ({
      ...state,
      statuses: getSelectedValues(options) as SingleMatch['status'][]
    }))
  }

  useEffect(() => {
    setLoading(true)

    api.searchMatches(searchFormValues).then(response => {
      setLoading(false)

      if (response) {
        setSearchMatchesData(response.content)
        setCurrentPage(response.number)
        setTotalPages(response.total_pages)
      }
    })
  }, [api, searchFormValues])

  useEffect(() => {
    dimmBackground()
    clearFieldErrors()
  }, [clearFieldErrors])

  return (
    <PageContainer>
      <Header>
        <h1>{copy.page.search.heading}</h1>
      </Header>

      <SearchScreenNavigation />

      <SearchForm id="search-matches-form">
        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.status}</span>
          <Multiselect options={statusOptions} onSelectionChange={handleStatusChange} />
        </InputLabel>

        <InputLabel hideErrorOnFocus={false}>
          <span>{texts_form.created_by}</span>
          <span>{fieldErrors.created_by}</span>
          <Input name="created_by" onInput={handleSearch} />
        </InputLabel>
      </SearchForm>

      {noResults && <h2 style={{ margin: 'auto' }}>{copy.common.no_search_results}</h2>}
      {spinnerIsVisible && <Spinner style={{ margin: 'auto' }} />}

      {tableIsVisible && (
        <SortSection>
          <span>{copy.page.search.search_results}</span>

          {/* Comment this Select till backend is ready
        <label>
          Sortuj po
          <Select>
            <option>Data ostatniej modyfikacji</option>
          </Select>
        </label> */}
        </SortSection>
      )}

      {tableIsVisible && (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>{texts_form.status}</th>
                <th>{texts_form.match_id}</th>
                <th>{texts_form.created_by}</th>
                <th>{texts_form.created_date}</th>
                <th>{texts_form.last_modified_date}</th>
                <th>{texts_form.customer_interest}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {searchMatchesData?.map(match => (
                <tr key={match.id}>
                  <td>
                    <Status statusType={match.status} />
                  </td>
                  <td>
                    <strong>{match.id}</strong>
                  </td>
                  <td>
                    <strong>{match.created_by}</strong>
                  </td>
                  <td>{formatDate(match.created_date)}</td>
                  <td>{formatDate(match.last_modified_date)}</td>
                  <td>
                    {match.interest_reported ? (
                      <InterestWrapper>
                        <CheckedInShield /> {copy.common.yes}
                      </InterestWrapper>
                    ) : (
                      copy.common.no_value
                    )}
                  </td>
                  <td>
                    <Button small onClick={() => handleGetMatch(match.id)}>
                      {copy.common.button.look}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      )}

      {paginationIsVisible && (
        <StyledPagination
          numberOfSteps={totalPages}
          currentStep={currentPage + 1}
          onChange={handlePageChange}
        />
      )}
    </PageContainer>
  )
}

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 36px;

  > h1 {
    font: var(--600-24);
  }

  > a {
    padding: 8px 16px;
    border: 1px solid;
    border-radius: 4px;

    color: var(--green);
    font: var(--600-13);
    text-decoration: none;
    text-transform: uppercase;
  }
`

const SearchForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  padding: 24px;
  background: var(--very-light-blue);

  > ${InputLabel} {
    flex: 1;
  }
`

const SortSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > span {
    font: var(--600-16);
  }

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const StyledPagination = styled(Pagination)`
  align-self: center;
`

const InterestWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  color: var(--green);
  font: var(--600-13);
`
